<script setup lang="ts">
	import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import StyledButton from '@/components/StyledButton.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useOtpUser from '../composables/useOtpUser'

	interface Props {
		initialEmail?: string
		isLogin?: boolean
	}

	const props = defineProps<Props>()

	const toast = useToast()
	const router = useRouter()

	const formOtp = reactive({
		email: props.initialEmail || '',
		otp: '',
	})

	const formOtpComputed = computed(() => ({
		...formOtp,
	}))

	const isVerified = ref(false)
	const canResend = ref(true)
	const resendCountdown = ref(0)

	const useOtpComputed = computed(() => useOtpUser({ email: formOtp.email }))

	let countdownInterval: NodeJS.Timeout | null = null

	const startResendCountdown = () => {
		canResend.value = false
		resendCountdown.value = 120

		if (countdownInterval) clearInterval(countdownInterval)
		countdownInterval = setInterval(() => {
			resendCountdown.value--
			if (resendCountdown.value <= 0) {
				canResend.value = true
				if (countdownInterval) clearInterval(countdownInterval)
			}
		}, 1000)
	}

	const handleSubmitOtp = async () => {
		if (formOtpComputed.value.otp.length !== 6) {
			toast.error('El código de verificación debe tener 6 dígitos.')
			return
		}

		if (formOtpComputed.value.email === '') {
			toast.error('El email es obligatorio.')
			return
		}

		try {
			const response = await useOtpComputed.value.postOptUserMutationAsync({
				email: formOtpComputed.value.email,
				otp: formOtpComputed.value.otp,
			})

			if (response.status === 204) {
				isVerified.value = true
				toast.success('¡Código verificado exitosamente!')
				window.setTimeout(() => {
					router.push({ name: 'dashboard' })
				}, 2000)
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message || 'Código inválido o expirado'
			)
		}
	}

	const handleOtpInput = (value: string) => {
		formOtp.otp = value.replace(/\D/g, '').slice(0, 6)
	}

	const resentOtp = async () => {
		if (!formOtpComputed.value.email) {
			toast.error('Por favor ingresa tu correo electrónico.')
			return
		}

		try {
			const response = await useOtpComputed.value.refetch()

			if (response) {
				toast.success('Código reenviado a tu correo electrónico.')
				startResendCountdown()
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message || 'Error al reenviar el código.'
			)
		}
	}

	const startCountdown = () => {
		startResendCountdown()
	}

	onUnmounted(() => {
		if (countdownInterval) clearInterval(countdownInterval)
	})

	const loading = computed(() => {
		return useOtpComputed.value.postOptUserMutationIsLoading
	})

	defineExpose({
		startCountdown,
		postOptUserMutationIsLoading: loading.value,
	})

	onMounted(() => {
		startCountdown()
	})
</script>

<template>
	<div
		v-if="!isVerified"
		class="space-y-3"
	>
		<div class="text-center mb-4">
			<h3 class="text-lg font-semibold text-gray-900 mb-1">
				Ingresa tu código de verificación
			</h3>
			<p class="text-sm text-gray-600">
				Código de <span class="font-bold">6 dígitos</span>
			</p>
		</div>

		<form
			@submit.prevent="handleSubmitOtp"
			class="space-y-3"
		>
			<StyledInput
				v-if="!isLogin"
				v-model="formOtp.email"
				label="Correo Electrónico"
				placeholder="tu@email.com"
			/>

			<StyledInput
				v-model="formOtp.otp"
				label="Código de un solo uso"
				placeholder="• • • • • •"
				type="text"
				:maxLength="6"
				:disabled="useOtpComputed.postOptUserMutationIsLoading.value"
				inputClass="text-center text-3xl tracking-[1rem] font-bold !py-4 focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100"
				@input="handleOtpInput"
				@keyup.enter="handleSubmitOtp"
			/>

			<!-- Barra de progreso de expiración -->
			<div class="space-y-1.5">
				<div class="flex items-center justify-between text-xs">
					<span class="text-gray-600 font-medium">Tiempo restante</span>
					<span class="font-bold text-blue-600">{{ resendCountdown }}s</span>
				</div>
				<div
					class="w-full bg-gray-200 rounded-full h-2 overflow-hidden shadow-inner"
				>
					<div
						class="h-2 rounded-full transition-all duration-1000 ease-linear"
						:class="{
							'bg-gradient-to-r from-green-500 to-green-600':
								resendCountdown > 60,
							'bg-gradient-to-r from-yellow-500 to-orange-500':
								resendCountdown <= 60 && resendCountdown > 30,
							'bg-gradient-to-r from-red-500 to-red-600': resendCountdown <= 30,
						}"
						:style="{
							width: `${(resendCountdown / 120) * 100}%`,
						}"
					></div>
				</div>
			</div>

			<StyledButton
				type="submit"
				label="Verificar"
				:loading="useOtpComputed.postOptUserMutationIsLoading.value"
				:disabled="
					formOtpComputed.otp.length !== 6 ||
					useOtpComputed.postOptUserMutationIsLoading.value
				"
				class="w-full !bg-blue-600 !text-white font-bold !py-4 text-base hover:!bg-blue-700 disabled:!bg-gray-300 disabled:!cursor-not-allowed !rounded-lg shadow-md hover:shadow-lg transition-all"
			/>
		</form>

		<!-- Info del código compacta -->
		<div class="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
			<div class="flex items-start gap-2">
				<svg
					class="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
					></path>
				</svg>
				<div class="flex-1 text-xs text-blue-700 space-y-0.5">
					<p>• Expira en <strong>2 minutos</strong></p>
					<p>• Revisa spam si no lo encuentras</p>
				</div>
			</div>
		</div>

		<!-- Botón de reenvío compacto -->
		<div class="text-center pt-2 border-t">
			<p class="text-xs text-gray-600 mb-2">¿No recibiste el código?</p>
			<button
				@click="resentOtp"
				:disabled="!canResend || !formOtpComputed.email"
				class="text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
			>
				{{ canResend ? 'Reenviar' : `Reenviar (${resendCountdown}s)` }}
			</button>
		</div>
	</div>

	<!-- Estado verificado compacto -->
	<div
		v-else
		class="bg-gradient-to-r from-green-400 to-green-600 rounded-lg p-4 text-white text-center"
	>
		<svg
			class="w-12 h-12 mx-auto mb-2"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path
				stroke-linecap="round"
				stroke-linejoin="round"
				stroke-width="2"
				d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
			></path>
		</svg>
		<h3 class="text-lg font-bold mb-1">¡Verificación Completa!</h3>
		<p class="text-green-50 text-xs">Redirigiendo...</p>
	</div>
</template>
